import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Bell,
  CheckCircle2,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  Inbox,
  Layers,
  Map as MapIcon,
  Plus,
  RefreshCw,
  Truck,
  Users,
  Volume2,
} from 'lucide-react';
import { apiGet, apiPatch } from '../shared/apiClient';
import type { Chiamata, Squadra } from '../shared/types';
import { STATI_CHIAMATA } from '../shared/constants';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { playEmergencyAlert } from '../utils/audioAlert';
import { getNotificationPermission, subscribeUserToPush } from '../utils/pushNotifications';
import { useSseStream } from '../shared/useSseStream';
import { CollapsibleSection } from '../components/CollapsibleSection';
import { MappaSquadraModal } from '../components/MappaSquadraModal';
import { getTurnoDateStrRome } from '../shared/tz';

function formatProblemaChiamata(c: { guastoSegnalato?: string | null; descrizione?: string | null }) {
  const guasto = c.guastoSegnalato?.trim();
  const desc = c.descrizione?.trim();
  if (guasto && desc && desc !== guasto && !desc.startsWith('Chiamata generata') && !desc.startsWith('Chiamata telefonica')) {
    return `[Guasto: ${guasto}] ${desc}`;
  }
  return guasto || desc || 'Nessun dettaglio';
}

export function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [permission, setPermission] = useState(getNotificationPermission());

  // Stato Dotazione Turno (Targa mezzo, Collaboratori e Autista)
  const [targaInput, setTargaInput] = useState('');
  const [selectedSquadre, setSelectedSquadre] = useState<string[]>([]);
  const [hasAltro, setHasAltro] = useState(false);
  const [altroInput, setAltroInput] = useState('');
  const [autista, setAutista] = useState('');
  const [dotazioneLoaded, setDotazioneLoaded] = useState(false);
  const [isSavedRecently, setIsSavedRecently] = useState(false);

  // Query propria squadra
  const { data: squadraMe } = useQuery<Squadra>({
    queryKey: ['squadra-me'],
    queryFn: async () => {
      const { data } = await apiGet<Squadra>('/squadre/me');
      return data;
    },
  });

  // Query altre squadre attive per "collaborato con"
  const { data: altreSquadreData } = useQuery<{ id: string; nome: string }[]>({
    queryKey: ['squadre-altre'],
    queryFn: async () => {
      const { data } = await apiGet<{ id: string; nome: string }[]>('/squadre/altre');
      return data;
    },
  });
  const altreSquadre = useMemo(() => altreSquadreData || [], [altreSquadreData]);

  const myTeamName = user?.nome || squadraMe?.nome || 'Squadra';

  // Inizializza campi targa, collaboratori e autista al primo caricamento
  useEffect(() => {
    if (squadraMe && !dotazioneLoaded) {
      setTargaInput(squadraMe.mezzoTarga || '');
      setAutista(squadraMe.autista || '');
      if (squadraMe.collaboratori) {
        const parts = squadraMe.collaboratori.split(',').map((s) => s.trim()).filter(Boolean);
        const matchedSquadre: string[] = [];
        const nonMatched: string[] = [];
        const otherNames = altreSquadre.map((s) => s.nome);
        for (const p of parts) {
          if (otherNames.includes(p)) {
            matchedSquadre.push(p);
          } else {
            nonMatched.push(p);
          }
        }
        setSelectedSquadre(matchedSquadre);
        if (nonMatched.length > 0) {
          setHasAltro(true);
          setAltroInput(nonMatched.join(', '));
        }
      }
      setDotazioneLoaded(true);
    }
  }, [squadraMe, altreSquadre, dotazioneLoaded]);

  // Mutation salvataggio dotazione squadra
  const dotazioneMutation = useMutation({
    mutationFn: async (payload: { mezzoTarga: string | null; collaboratori: string | null; autista: string | null; dotazioneTurno?: string | null }) => {
      const { data } = await apiPatch<Squadra>('/squadre/me/dotazione', payload);
      return data;
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(['squadra-me'], updated);
      setIsSavedRecently(true);
      setTimeout(() => setIsSavedRecently(false), 2000);
    },
    onError: () => {
      toast({ type: 'error', message: 'Errore salvataggio dotazione mezzo/collaboratori/autista' });
    },
  });

  const dotazioneDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (dotazioneDebounceRef.current) clearTimeout(dotazioneDebounceRef.current);
    };
  }, []);

  const saveDotazione = (targa: string, squadre: string[], altroActive: boolean, altroText: string, currentAutista: string) => {
    const collabList = [...squadre, ...(altroActive && altroText.trim() ? [altroText.trim()] : [])];
    const collabStr = collabList.join(', ');
    const effectiveAutista = currentAutista.trim() || myTeamName;
    const turnoOggi = getTurnoDateStrRome();
    dotazioneMutation.mutate({
      mezzoTarga: targa.trim() || null,
      collaboratori: collabStr.trim() || null,
      autista: effectiveAutista.trim() || null,
      dotazioneTurno: turnoOggi,
    });
  };

  const debouncedSaveDotazione = (targa: string, squadre: string[], altroActive: boolean, altroText: string, currentAutista: string) => {
    if (dotazioneDebounceRef.current) clearTimeout(dotazioneDebounceRef.current);
    dotazioneDebounceRef.current = setTimeout(() => {
      saveDotazione(targa, squadre, altroActive, altroText, currentAutista);
    }, 500);
  };

  const handleTargaChange = (val: string) => {
    setTargaInput(val);
    debouncedSaveDotazione(val, selectedSquadre, hasAltro, altroInput, autista);
  };

  const toggleSquadraCollab = (nome: string) => {
    if (dotazioneDebounceRef.current) clearTimeout(dotazioneDebounceRef.current);
    const next = selectedSquadre.includes(nome)
      ? selectedSquadre.filter((n) => n !== nome)
      : [...selectedSquadre, nome];
    setSelectedSquadre(next);
    const nextCollabs = [...next, ...(hasAltro && altroInput.trim() ? [altroInput.trim()] : [])];
    const nextAutista = (autista && nextCollabs.includes(autista)) ? autista : myTeamName;
    setAutista(nextAutista);
    saveDotazione(targaInput, next, hasAltro, altroInput, nextAutista);
  };

  const toggleAltro = () => {
    if (dotazioneDebounceRef.current) clearTimeout(dotazioneDebounceRef.current);
    const nextHasAltro = !hasAltro;
    setHasAltro(nextHasAltro);
    const nextCollabs = [...selectedSquadre, ...(nextHasAltro && altroInput.trim() ? [altroInput.trim()] : [])];
    const nextAutista = (autista && nextCollabs.includes(autista)) ? autista : myTeamName;
    setAutista(nextAutista);
    saveDotazione(targaInput, selectedSquadre, nextHasAltro, altroInput, nextAutista);
  };

  const handleAltroChange = (val: string) => {
    setAltroInput(val);
    const nextCollabs = [...selectedSquadre, ...(hasAltro && val.trim() ? [val.trim()] : [])];
    const nextAutista = (autista && nextCollabs.includes(autista)) ? autista : myTeamName;
    setAutista(nextAutista);
    debouncedSaveDotazione(targaInput, selectedSquadre, hasAltro, val, nextAutista);
  };

  const handleSelectAutista = (val: string) => {
    if (dotazioneDebounceRef.current) clearTimeout(dotazioneDebounceRef.current);
    setAutista(val);
    saveDotazione(targaInput, selectedSquadre, hasAltro, altroInput, val);
  };

  const collabList = [...selectedSquadre, ...(hasAltro && altroInput.trim() ? [altroInput.trim()] : [])];
  const autistiDisponibili = Array.from(new Set([myTeamName, ...collabList]));
  const currentAutista = (autista && autistiDisponibili.includes(autista)) ? autista : myTeamName;

  const notifiedCallIdsRef = useRef<Set<string>>(new Set());
  const isInitialPendingLoadRef = useRef(true);

  const { data, refetch, isFetching } = useQuery({
    queryKey: ['pending-calls'],
    queryFn: async () => {
      const { data } = await apiGet<Chiamata[]>('/chiamate/pending');
      return data;
    },
    refetchInterval: 15_000,
  });

  const chiamate = useMemo(() => data || [], [data]);

  const [showMappaModal, setShowMappaModal] = useState(false);

  const chiamateConCoord = useMemo(() => {
    return chiamate.filter((c) => {
      const lat = c.impianto?.lat;
      const lng = c.impianto?.lng;
      return typeof lat === 'number' && typeof lng === 'number' && lat !== 0 && lng !== 0;
    });
  }, [chiamate]);

  const chiamataInCarico = useMemo(() => {
    return chiamate.find((c) => c.inLavorazione);
  }, [chiamate]);

  // Rileva arrivo di nuove chiamate in tempo reale (polling / riattivazione schermo) e suona l'allarme solo per chiamate mai viste
  useEffect(() => {
    if (data) {
      if (isInitialPendingLoadRef.current) {
        // Al primo caricamento (o refresh pagina) memorizziamo gli ID già esistenti per non suonare a vuoto
        data.forEach((c) => notifiedCallIdsRef.current.add(c.id));
        isInitialPendingLoadRef.current = false;
      } else {
        const unnotifiedCalls = data.filter((c) => !notifiedCallIdsRef.current.has(c.id));
        if (unnotifiedCalls.length > 0) {
          unnotifiedCalls.forEach((c) => notifiedCallIdsRef.current.add(c.id));
          playEmergencyAlert();
          toast({
            type: 'info',
            message: '🚨 Nuova chiamata assegnata alla squadra!',
          });
        }
      }
    }
  }, [data, toast]);

  // Sottoscrizione automatica notifica Push W3C all'avvio (se permesso non negato)
  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission !== 'denied') {
      subscribeUserToPush().then(() => {
        setPermission(getNotificationPermission());
      });
    }
  }, []);

  // Ascolto stream permanente SSE (Server-Sent Events) per aggiornamento live 0s ad app in focus
  useSseStream((evt) => {
    queryClient.invalidateQueries({ queryKey: ['pending-calls'] });
    queryClient.invalidateQueries({ queryKey: ['chiamate-aperte'] });
    queryClient.invalidateQueries({ queryKey: ['chiamate-da-terminare'] });
    queryClient.invalidateQueries({ queryKey: ['squadra-chiuse-oggi'] });
    if (evt.type === 'chiamata_assegnata') {
      const chiamataId = evt.chiamata?.id || (evt as any).chiamataId;
      if (!chiamataId || !notifiedCallIdsRef.current.has(chiamataId)) {
        if (chiamataId) notifiedCallIdsRef.current.add(chiamataId);
        playEmergencyAlert();
        toast({
          type: 'info',
          message: evt.title || '🚨 Nuova chiamata assegnata alla squadra!',
        });
      }
    }
  });

  const handleEnablePush = async () => {
    const res = await subscribeUserToPush();
    setPermission(getNotificationPermission());
    if (res.ok) {
      toast({ type: 'success', message: 'Notifiche e allarme sonoro attivati!' });
      playEmergencyAlert();
    } else {
      toast({ type: 'error', message: res.message || 'Impossibile attivare le notifiche' });
    }
  };

  const { data: impostazioniSquadra } = useQuery<{ mostraDaTerminareSquadre: boolean; mostraAperteSquadre: boolean; permettiRifiutoChiamate: boolean }>({
    queryKey: ['squadra-impostazioni'],
    queryFn: async () => {
      const { data } = await apiGet<{ mostraDaTerminareSquadre: boolean; mostraAperteSquadre: boolean; permettiRifiutoChiamate: boolean }>('/squadre/impostazioni');
      return data;
    },
    staleTime: 30_000,
  });
  const mostraDaTerminare = impostazioniSquadra?.mostraDaTerminareSquadre ?? true;
  const mostraAperte = impostazioniSquadra?.mostraAperteSquadre ?? true;

  const { data: aperteData } = useQuery({
    queryKey: ['chiamate-aperte'],
    queryFn: async () => {
      const { data } = await apiGet<Chiamata[]>('/chiamate/aperte');
      return data;
    },
    enabled: mostraAperte,
    refetchInterval: 30_000,
  });
  const chiamateAperte = mostraAperte ? (aperteData || []) : [];

  const { data: daTerminareData } = useQuery({
    queryKey: ['chiamate-da-terminare'],
    queryFn: async () => {
      const { data } = await apiGet<Chiamata[]>('/chiamate/da-terminare');
      return data;
    },
    enabled: mostraDaTerminare,
    refetchInterval: 30_000,
  });
  const chiamateDaTerminare = mostraDaTerminare ? (daTerminareData || []) : [];

  // Selettore Turno Operativo (05:00 -> 05:00, default turno corrente offset 0)
  const [offsetTurno, setOffsetTurno] = useState<number>(0);

  const labelTurno = useMemo(() => {
    const now = new Date();
    const romeDateStr = now.toLocaleDateString('en-CA', { timeZone: 'Europe/Rome' });
    const romeHourStr = now.toLocaleTimeString('en-US', { timeZone: 'Europe/Rome', hour: 'numeric', hour12: false });
    const romeHour = parseInt(romeHourStr, 10);

    const d = new Date(`${romeDateStr}T12:00:00.000Z`);
    if (romeHour < 5) {
      d.setUTCDate(d.getUTCDate() - 1);
    }
    d.setUTCDate(d.getUTCDate() + offsetTurno);

    return d.toLocaleDateString('it-IT', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      timeZone: 'Europe/Rome',
    });
  }, [offsetTurno]);

  // Contatori dashboard: chiuse nel turno e da terminare nel turno (sempre visibili per la squadra)
  const { data: chiuseOggiData } = useQuery({
    queryKey: ['squadra-chiuse-oggi', offsetTurno],
    queryFn: async () => {
      const { data } = await apiGet<Chiamata[]>(`/chiamate/squadra/chiuse-oggi?offsetTurno=${offsetTurno}`);
      return data;
    },
    refetchInterval: 30_000,
  });
  const chiamateChiuseOggi = chiuseOggiData || [];

  // Categoria attiva dei contatori (tap → mostra lista sotto). Default: assegnate.
  const [categoriaAttiva, setCategoriaAttiva] = useState<'aperte' | 'assegnate' | 'chiuse' | null>('assegnate');

  const onRefresh = async () => {
    await Promise.all([
      refetch(),
      queryClient.invalidateQueries({ queryKey: ['chiamate-aperte'] }),
      queryClient.invalidateQueries({ queryKey: ['chiamate-da-terminare'] }),
      queryClient.invalidateQueries({ queryKey: ['squadra-chiuse-oggi'] }),
    ]);
    toast({ type: 'info', message: 'Dati aggiornati' });
  };

  return (
    <div className="space-y-4 animate-fade-in">
      {/* ── BANNER ATTIVAZIONE NOTIFICHE SMARTPHONE ── */}
      {permission === 'default' && (
        <div className="rounded-2xl border border-amber-300 bg-linear-to-r from-amber-500/10 via-amber-500/5 to-transparent p-4 flex items-center justify-between gap-3 shadow-xs">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0 shadow-xs">
              <Bell className="w-5 h-5 animate-bounce" />
            </div>
            <div className="min-w-0">
              <h4 className="font-extrabold text-sm text-slate-900 leading-snug">
                Attiva le Notifiche & Allarme
              </h4>
              <p className="text-xs text-slate-600 truncate">
                Fai suonare il telefono quando l'operatore ti assegna una chiamata
              </p>
            </div>
          </div>
          <button
            type="button"
            className="btn-primary text-xs font-bold py-2 px-3 shrink-0 gap-1.5 shadow-xs"
            onClick={handleEnablePush}
          >
            <Volume2 className="w-3.5 h-3.5" />
            <span>Attiva</span>
          </button>
        </div>
      )}

      {/* ── CARD DOTAZIONE MEZZO E COLLABORAZIONE TURNO (COLLASSABILE) ── */}
      <CollapsibleSection
        icon={Truck}
        iconClass="bg-blue-100 text-blue-700"
        title="Dotazione Mezzo & Collaborazione di Turno"
        subtitle={
          (targaInput || autista || selectedSquadre.length > 0 || (hasAltro && altroInput)) ? (
            <span className="inline-flex items-center gap-1.5 flex-wrap">
              {targaInput && (
                <span className="bg-blue-100/90 text-blue-900 font-extrabold px-2 py-0.5 rounded-md border border-blue-300/80">
                  🚗 {targaInput}
                </span>
              )}
              {autista && (
                <span className="bg-amber-100/90 text-amber-950 font-extrabold px-2 py-0.5 rounded-md border border-amber-300/80">
                  👤 Guida: {autista}
                </span>
              )}
              {(selectedSquadre.length > 0 || (hasAltro && altroInput)) && (
                <span className="bg-slate-100 text-slate-900 font-extrabold px-2 py-0.5 rounded-md border border-slate-300/80">
                  🤝 Collab: {[...selectedSquadre, ...(hasAltro && altroInput ? [altroInput] : [])].join(', ')}
                </span>
              )}
            </span>
          ) : (
            'Targa mezzo, collaboratori e guida del turno'
          )
        }
        headerRight={
          isSavedRecently ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200 animate-fade-in">
              <CheckCircle2 className="w-2.5 h-2.5 text-emerald-600" /> Salvato
            </span>
          ) : dotazioneMutation.isPending ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-ping" /> Salvataggio...
            </span>
          ) : squadraMe?.dotazioneTurno === getTurnoDateStrRome() ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
              ✓ Confermato oggi
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-300">
              ⚠️ Da confermare
            </span>
          )
        }
        defaultOpen={Boolean(squadraMe && squadraMe.dotazioneTurno !== getTurnoDateStrRome())}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-0.5">
          {/* Campo 1: Targa del mezzo */}
          <div className="space-y-1">
            <label className="block text-[11px] font-bold text-slate-700 flex items-center gap-1">
              <Truck className="w-3 h-3 text-blue-600" />
              <span>Targa mezzo:</span>
            </label>
            <input
              type="text"
              value={targaInput}
              onChange={(e) => handleTargaChange(e.target.value)}
              placeholder="es. AB 123 CD..."
              className="w-full px-2.5 py-1.5 text-xs font-semibold text-slate-800 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-hidden transition"
            />
          </div>

          {/* Campo 2: Collaborato con */}
          <div className="space-y-1">
            <label className="block text-[11px] font-bold text-slate-700 flex items-center gap-1">
              <Users className="w-3 h-3 text-blue-600" />
              <span>Collaborato con:</span>
            </label>

            {/* Pills selezione multipla */}
            <div className="flex flex-wrap gap-1 items-center">
              {altreSquadre.map((sq) => {
                const isSelected = selectedSquadre.includes(sq.nome);
                return (
                  <button
                    key={sq.id}
                    type="button"
                    onClick={() => toggleSquadraCollab(sq.nome)}
                    className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition flex items-center gap-1 border active:scale-95 ${
                      isSelected
                        ? 'bg-blue-600 text-white border-blue-600 shadow-xs'
                        : 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200'
                    }`}
                  >
                    <span>{sq.nome}</span>
                    {isSelected && <span className="text-[9px]">✓</span>}
                  </button>
                );
              })}

              {/* Opzione Altro */}
              <button
                type="button"
                onClick={toggleAltro}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition flex items-center gap-1 border active:scale-95 ${
                  hasAltro
                    ? 'bg-blue-600 text-white border-blue-600 shadow-xs'
                    : 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200'
                }`}
              >
                <span>Altro</span>
                {hasAltro && <span className="text-[9px]">✓</span>}
              </button>
            </div>

            {/* Input testo per "Altro" */}
            {hasAltro && (
              <div className="pt-0.5 animate-fade-in">
                <input
                  type="text"
                  value={altroInput}
                  onChange={(e) => handleAltroChange(e.target.value)}
                  placeholder="Nome collaboratore..."
                  className="w-full px-2.5 py-1 text-xs font-semibold text-slate-800 bg-white border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-hidden"
                />
              </div>
            )}
          </div>
        </div>

        {/* Selezione Autista / Guida del mezzo */}
        <div className="pt-2 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3">
          <label className="text-[11px] font-bold text-slate-700 shrink-0 flex items-center gap-1">
            <span>Guida / Autista:</span>
          </label>
          <div className="flex flex-wrap gap-1 items-center">
            {autistiDisponibili.map((nome) => {
              const isSelected = currentAutista === nome;
              return (
                <button
                  key={nome}
                  type="button"
                  onClick={() => handleSelectAutista(nome)}
                  className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition flex items-center gap-1 border active:scale-95 ${
                    isSelected
                      ? 'bg-amber-500 text-white border-amber-500 shadow-xs'
                      : 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200'
                  }`}
                >
                  <span>{nome}</span>
                  {isSelected && <span className="text-[9px]">✓</span>}
                </button>
              );
            })}
          </div>
        </div>

        {squadraMe?.dotazioneTurno !== getTurnoDateStrRome() && (
          <div className="pt-2 border-t border-slate-100 flex justify-end">
            <button
              type="button"
              disabled={dotazioneMutation.isPending}
              onClick={() => saveDotazione(targaInput, selectedSquadre, hasAltro, altroInput, autista)}
              className="btn-primary py-1.5 px-3.5 text-xs font-extrabold bg-blue-600 hover:bg-blue-700 text-white shadow-xs gap-1.5"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>Conferma composizione per il turno di oggi</span>
            </button>
          </div>
        )}
      </CollapsibleSection>

      {/* ── CARD PRINCIPALE DASHBOARD & STATISTICHE ── */}
      <section className="card-section">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="page-title">Dashboard</h2>
            <p className="text-xs sm:text-sm text-semaforo-dark-500 mt-0.5">
              Panoramica interventi e chiamate della squadra
            </p>
          </div>
          <div className="flex items-center gap-2 self-start sm:self-auto shrink-0">
            <button
              type="button"
              className={`btn-outline px-3 py-2 text-xs font-bold gap-1.5 shadow-2xs transition cursor-pointer ${
                chiamate.length > 0
                  ? 'btn-mappa-pulse text-emerald-950 bg-emerald-100 border-emerald-400'
                  : 'text-slate-700 bg-slate-50 hover:bg-slate-100 border-slate-300'
              }`}
              onClick={() => setShowMappaModal(true)}
              title="Apri mappa impianti assegnati"
            >
              <MapIcon className={`w-3.5 h-3.5 ${chiamate.length > 0 ? 'text-emerald-700' : 'text-slate-500'}`} />
              <span>Mappa ({chiamateConCoord.length})</span>
              {chiamataInCarico ? (
                <span className="w-2 h-2 rounded-full bg-emerald-600 animate-ping ml-0.5" />
              ) : chiamate.length > 0 ? (
                <span className="w-2 h-2 rounded-full bg-emerald-500 ml-0.5" />
              ) : null}
            </button>
            <button
              type="button"
              className="btn-outline px-3 py-2 text-xs font-bold gap-1.5 shadow-2xs"
              onClick={onRefresh}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin text-semaforo-verde-700' : ''}`} />
              <span>Aggiorna</span>
            </button>
          </div>
        </div>

        {/* Selettore Turno Operativo (05:00 -> 05:00) */}
        <div className="mt-3.5 flex items-center justify-between p-2 rounded-xl bg-slate-200/50 border border-slate-300/80 shadow-inner">
          <button
            type="button"
            onClick={() => setOffsetTurno((prev) => prev - 1)}
            className="p-1.5 px-2.5 rounded-lg bg-white border border-slate-300 shadow-xs hover:bg-slate-50 active:scale-95 transition flex items-center gap-1 text-xs font-bold text-slate-700"
            title="Turno precedente"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden xs:inline">Turno prec.</span>
          </button>

          <div className="text-center px-2 min-w-0">
            <div className="text-xs sm:text-sm font-extrabold text-slate-900 truncate">
              {offsetTurno === 0 ? '🟢 Turno Attuale (05:00 → 05:00)' : `📅 Turno: ${labelTurno} (05:00 → 05:00)`}
            </div>
            {offsetTurno !== 0 && (
              <button
                type="button"
                onClick={() => setOffsetTurno(0)}
                className="text-[10px] font-bold text-indigo-600 hover:underline inline-block mt-0.5"
              >
                Torna al turno attuale
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={() => setOffsetTurno((prev) => Math.min(0, prev + 1))}
            disabled={offsetTurno >= 0}
            className={`p-1.5 px-2.5 rounded-lg border shadow-xs transition flex items-center gap-1 text-xs font-bold ${
              offsetTurno >= 0
                ? 'bg-slate-100 border-transparent text-slate-300 cursor-not-allowed'
                : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50 active:scale-95'
            }`}
            title="Turno successivo"
          >
            <span className="hidden xs:inline">Turno succ.</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Contatori dashboard: ordine Aperte · Assegnate · Chiuse */}
        <div className="mt-3.5 grid grid-cols-3 gap-2 sm:gap-3">
          {/* 1. APERTE (a sinistra di tutto) */}
          <button
            type="button"
            disabled={!mostraAperte}
            onClick={() => mostraAperte && setCategoriaAttiva(categoriaAttiva === 'aperte' ? null : 'aperte')}
            className={`rounded-xl p-2.5 sm:p-3.5 text-left transition border shadow-xs ${
              !mostraAperte
                ? 'opacity-60 cursor-not-allowed border-slate-200 bg-slate-50'
                : chiamateAperte.some((c) => c.urgente)
                ? 'border-rose-400 bg-rose-50 shadow-md ring-2 ring-rose-500/40 animate-pulse'
                : chiamateAperte.some((c) => c.daCompletare)
                ? 'border-amber-400 bg-amber-50 shadow-md ring-2 ring-amber-400/40 animate-pulse'
                : chiamateAperte.some((c) => c.daSegnalazione)
                ? 'border-emerald-400 bg-emerald-50 shadow-md ring-2 ring-emerald-400/40 animate-pulse'
                : categoriaAttiva === 'aperte'
                ? 'border-violet-500 bg-violet-50 shadow-md ring-2 ring-violet-400/30'
                : 'border-slate-300/90 bg-linear-to-b from-white to-slate-50 hover:border-slate-400'
            }`}
          >
            <div className="flex items-center justify-between gap-1">
              <span className="text-[10px] sm:text-xs font-bold text-violet-800 uppercase tracking-wider flex items-center gap-1.5">
                <span className={`ln-status-dot ${
                  chiamateAperte.some((c) => c.urgente)
                    ? 'bg-rose-500'
                    : chiamateAperte.some((c) => c.daCompletare)
                    ? 'bg-amber-500'
                    : chiamateAperte.some((c) => c.daSegnalazione)
                    ? 'bg-emerald-500'
                    : 'bg-violet-500'
                } shrink-0`} />
                Aperte
              </span>
              <Inbox className="w-3.5 h-3.5 text-violet-600 shrink-0 hidden sm:block" />
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-1">
              {mostraAperte ? chiamateAperte.length : 0}
            </div>
            <div className="text-[10px] sm:text-[11px] text-slate-500 font-medium truncate">
              {mostraAperte ? 'Disponibili' : 'Disabilitate'}
            </div>
          </button>

          {/* 2. ASSEGNATE */}
          <button
            type="button"
            onClick={() => setCategoriaAttiva(categoriaAttiva === 'assegnate' ? null : 'assegnate')}
            className={`rounded-xl p-2.5 sm:p-3.5 text-left transition border shadow-xs ${
              chiamate.some((c) => c.urgente)
                ? 'border-rose-400 bg-rose-50 shadow-md ring-2 ring-rose-500/40 animate-pulse'
                : chiamate.some((c) => c.daCompletare)
                ? 'border-amber-400 bg-amber-50 shadow-md ring-2 ring-amber-400/40 animate-pulse'
                : chiamate.some((c) => c.daSegnalazione && c.stato !== 'chiusa' && c.stato !== 'annullata')
                ? 'border-emerald-400 bg-emerald-50 shadow-md ring-2 ring-emerald-400/40 animate-pulse'
                : categoriaAttiva === 'assegnate'
                ? 'border-sky-500 bg-sky-50 shadow-md ring-2 ring-sky-400/30'
                : 'border-slate-300/90 bg-linear-to-b from-white to-slate-50 hover:border-slate-400'
            }`}
          >
            <div className="flex items-center justify-between gap-1">
              <span className="text-[10px] sm:text-xs font-bold text-sky-800 uppercase tracking-wider flex items-center gap-1.5">
                <span className={`ln-status-dot ${
                  chiamate.some((c) => c.urgente)
                    ? 'bg-rose-500'
                    : chiamate.some((c) => c.daCompletare)
                    ? 'bg-amber-500'
                    : chiamate.some((c) => c.daSegnalazione && c.stato !== 'chiusa' && c.stato !== 'annullata')
                    ? 'bg-emerald-500'
                    : 'bg-sky-500'
                } shrink-0`} />
                Assegnate
              </span>
              <Layers className="w-3.5 h-3.5 text-sky-600 shrink-0 hidden sm:block" />
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-1">{chiamate.length}</div>
            <div className="text-[10px] sm:text-[11px] text-slate-500 font-medium truncate">In carico</div>
          </button>

          {/* 3. CHIUSE */}
          <button
            type="button"
            onClick={() => setCategoriaAttiva(categoriaAttiva === 'chiuse' ? null : 'chiuse')}
            className={`rounded-xl p-2.5 sm:p-3.5 text-left transition border shadow-xs ${
              categoriaAttiva === 'chiuse'
                ? 'border-emerald-500 bg-emerald-50 shadow-md ring-2 ring-emerald-400/30'
                : 'border-slate-300/90 bg-linear-to-b from-white to-slate-50 hover:border-slate-400'
            }`}
          >
            <div className="flex items-center justify-between gap-1">
              <span className="text-[10px] sm:text-xs font-bold text-emerald-800 uppercase tracking-wider flex items-center gap-1.5">
                <span className="ln-status-dot bg-emerald-500 shrink-0" />
                Chiuse
              </span>
              <CheckSquare className="w-3.5 h-3.5 text-emerald-600 shrink-0 hidden sm:block" />
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-1">{chiamateChiuseOggi.length}</div>
            <div className="text-[10px] sm:text-[11px] text-slate-500 font-medium truncate">{offsetTurno === 0 ? 'Turno Oggi' : labelTurno}</div>
          </button>
        </div>

        {/* Lista della categoria selezionata (tap sul contatore) */}
        {categoriaAttiva && (
          <div className="mt-3 pt-3 border-t border-slate-100 animate-fade-in">
            {categoriaAttiva === 'aperte' && (
              chiamateAperte.length === 0 ? (
                <p className="text-xs text-slate-500 text-center py-3">Nessuna chiamata aperta disponibile</p>
              ) : (
                <div className="space-y-2">
                  {chiamateAperte.map((c) => (
                    <ChiamataRiga
                      key={c.id}
                      c={c}
                      onClick={() => navigate(`/chiamata/${c.id}`)}
                    />
                  ))}
                </div>
              )
            )}
            {categoriaAttiva === 'assegnate' && (
              chiamate.length === 0 ? (
                <p className="text-xs text-slate-500 text-center py-3">Nessuna chiamata assegnata</p>
              ) : (
                <div className="space-y-2">
                  {chiamate.map((c) => (
                    <ChiamataRiga
                      key={c.id}
                      c={c}
                      onClick={() => navigate(`/chiamata/${c.id}`)}
                    />
                  ))}
                </div>
              )
            )}
            {categoriaAttiva === 'chiuse' && (
              chiamateChiuseOggi.length === 0 ? (
                <p className="text-xs text-slate-500 text-center py-3">Nessuna chiamata chiusa oggi</p>
              ) : (
                <div className="space-y-2">
                  {chiamateChiuseOggi.map((c) => (
                    <ChiamataRiga key={c.id} c={c} onClick={() => navigate(`/chiamata/${c.id}`)} />
                  ))}
                </div>
              )
            )}
          </div>
        )}

        {/* CTA Nuova chiamata */}
        <Link
          to="/nuova"
          className="btn-primary w-full justify-center mt-3.5 py-3 text-sm sm:text-base gap-2 shadow-md hover:shadow-lg transition active:scale-[0.98]"
        >
          <Plus className="w-5 h-5" />
          <span>Nuova Chiamata</span>
        </Link>
      </section>

      {/* ── SEZIONE CHIAMATE DA TERMINARE DISPONIBILI IN CODA ── */}
      {mostraDaTerminare && chiamateDaTerminare.length > 0 && (
        <CollapsibleSection
          icon={AlertCircle}
          iconClass="bg-amber-100 text-amber-800"
          title="Chiamate Da Terminare in Coda"
          subtitle={`${chiamateDaTerminare.length} interventi parziali in attesa di completamento`}
          defaultOpen={false}
        >
          <div className="mt-3.5 pt-3.5 border-t border-slate-100 space-y-2 animate-fade-in">
            {chiamateDaTerminare.map((c) => (
              <ChiamataRiga
                key={c.id}
                c={c}
                onClick={() => navigate(`/chiamata/${c.id}`)}
              />
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* ── MODAL MAPPA IMPIANTI ASSEGNATI IN SOVRAIMPRESSIONE ── */}
      <MappaSquadraModal
        isOpen={showMappaModal}
        onClose={() => setShowMappaModal(false)}
        chiamate={chiamate}
        onSelectChiamata={(id) => navigate(`/chiamata/${id}`)}
      />
    </div>
  );
}


// Riga compatta per le liste dei contatori (assegnate / chiuse oggi / da terminare oggi)
function ChiamataRiga({ c, onClick }: { c: Chiamata; onClick: () => void }) {
  const stato = STATI_CHIAMATA[c.stato];
  const repSquadra = (c.reports || [])
    .filter((r) => r.redattoDa !== 'operatore')
    .sort((a, b) => (a.sequenza || 0) - (b.sequenza || 0))
    .pop();
  const orarioSquadra = repSquadra?.fineLavoroAt || repSquadra?.chiusoAt || repSquadra?.createdAt;
  const orarioRif =
    c.stato === 'chiusa' || c.stato === 'sospesa' || c.stato === 'report_squadra'
      ? (orarioSquadra || c.chiusaAt)
      : c.dataOra;
  const dataRif = orarioRif
    ? new Date(orarioRif).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' })
    : '';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-xl border ${
        c.urgente
          ? 'border-rose-400 bg-rose-50/70 shadow-xs ring-2 ring-rose-500/40 animate-pulse'
          : c.daCompletare
          ? 'border-amber-400 bg-amber-50/70 shadow-xs ring-2 ring-amber-400/40 animate-pulse'
          : c.daSegnalazione && c.stato !== 'chiusa' && c.stato !== 'annullata'
          ? 'border-emerald-400 bg-emerald-50/70 shadow-xs ring-2 ring-emerald-400/40 animate-pulse'
          : 'border-slate-300/80 bg-linear-to-b from-white to-slate-50/70'
      } p-3.5 shadow-sm hover:border-indigo-300 hover:shadow-md active:scale-[0.99] transition flex items-center justify-between gap-3`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
          <span className="ln-impianto-chip text-[11px] py-0.5 px-2 font-bold">
            [{c.impianto?.codice}]
          </span>
          <span className={`ln-pill text-[10px] py-0.5 px-2 ${
            c.stato === 'chiusa'
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : c.stato === 'report_squadra'
              ? 'bg-amber-50 text-amber-800 border-amber-200'
              : c.stato === 'sospesa'
              ? 'bg-amber-50 text-amber-800 border-amber-200'
              : 'bg-rose-50 text-rose-700 border-rose-200'
          }`}>
            <span className={`ln-status-dot ${
              c.stato === 'chiusa' ? 'bg-emerald-500' : (c.stato === 'sospesa' || c.stato === 'report_squadra') ? 'bg-amber-500' : 'bg-rose-500'
            }`} />
            {stato?.label || c.stato}
          </span>
          {c.inLavorazione && (
            <span className="inline-flex items-center gap-1 text-[10px] font-extrabold text-purple-700 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded-md">
              ● In Lavorazione
            </span>
          )}
          {c.urgente && (
            <span className="inline-flex items-center gap-1 text-[10px] font-extrabold text-rose-800 bg-rose-100 border border-rose-300 px-2 py-0.5 rounded-md animate-pulse">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-600 animate-ping" />
              URGENTE
            </span>
          )}
          {c.daCompletare && !c.urgente && (
            <span className="inline-flex items-center gap-1 text-[10px] font-extrabold text-amber-800 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded-md">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-ping" />
              Da completare
            </span>
          )}
          {c.daSegnalazione && !c.urgente && !c.daCompletare && (
            <span className="inline-flex items-center gap-1 text-[10px] font-extrabold text-emerald-800 bg-emerald-100 border border-emerald-300 px-2 py-0.5 rounded-md animate-pulse">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
              Da Segnalazione
            </span>
          )}
          <span className="text-[11px] text-slate-400 ml-auto">{dataRif}</span>
        </div>
        <div className="font-bold text-sm text-slate-900 leading-snug break-words">
          {c.impianto?.nome}
        </div>
        <p className="text-xs text-slate-600 line-clamp-2 mt-0.5 font-normal">
          {formatProblemaChiamata(c)}
        </p>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
    </button>
  );
}